import { useEffect, useRef, useState } from "react";
import { CloseCircleGreyIcon } from "assets";
import { ModalPortal, MODAL_LAYER } from "components/modal-portal";
import { useClickOutside } from "hooks";

interface PaymentEmbedProps {
  show: boolean;
  close: () => void;
  onSuccess: (reference?: string) => void;
  onDecline?: () => void;
  onClose?: () => void;
  paymentURL: string;
}

const PaymentEmbed = ({ show, close, paymentURL, onSuccess, onDecline, onClose }: PaymentEmbedProps) => {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  useClickOutside(modalRef, close, []);
  const [loading, setLoading] = useState(true);

  // Callers pass inline handlers, so keep them in a ref — depending on them
  // directly would re-run the effect every render and flip the spinner back on
  // over an already-loaded iframe.
  const handlersRef = useRef({ onSuccess, onDecline, onClose });
  handlersRef.current = { onSuccess, onDecline, onClose };

  // The callback URL loads our SPA inside the frame, and any further navigation
  // fires another load event. Report completion once per payment.
  const completedRef = useRef(false);

  const reportSuccess = (reference?: string) => {
    if (completedRef.current) return;
    completedRef.current = true;
    handlersRef.current.onSuccess(reference);
  };

  /**
   * Paystack's hosted checkout does not postMessage when it finishes — it
   * redirects the frame to the callback_url we sent. That redirect landing back
   * on our own origin is the only signal we get that the flow is over, so watch
   * each iframe navigation and check whether we're home again.
   */
  const handleIframeLoad = () => {
    setLoading(false);

    let href: string | undefined;
    try {
      href = iframeRef.current?.contentWindow?.location?.href;
    } catch {
      // Still on checkout.paystack.com — cross-origin reads throw, as expected.
      return;
    }

    if (!href || !href.startsWith(window.location.origin)) return;

    // Paystack appends ?trxref=<ref>&reference=<ref> to the callback URL.
    const params = new URL(href).searchParams;
    reportSuccess(
      params.get("reference") ?? params.get("trxref") ?? undefined
    );
  };

  useEffect(() => {
    // Only the visible embed should listen. A hidden instance — the provider
    // mounts one globally — would otherwise also fire on a success message and
    // report a second completion for a payment it isn't running.
    if (!show) return;

    setLoading(true);
    completedRef.current = false;

    const handleMessage = (e: MessageEvent) => {
      // Listen for Paystack messages - they may come from any origin during redirect flow
      if (e.data?.event === "success" || e.data?.status === "success") {
        reportSuccess();
      } else if (e.data?.event === "decline") {
        handlersRef.current.onDecline?.();
      } else if (e.data?.event === "close") {
        handlersRef.current.onClose?.();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, paymentURL]);

  if (!show) return null;

  return (
    <ModalPortal>
    <div className="fixed inset-0 flex items-center justify-center bg-black/50" style={{ zIndex: MODAL_LAYER.payment }}>
      <div
        ref={modalRef}
        className="relative xl:w-[30%] lg:w-[40%] md:w-[50%] w-[90%] max-h-[90dvh] bg-white rounded-2xl p-6 overflow-hidden"
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-[#012169]">Checkout</h2>
          <button onClick={close} className="hover:opacity-80 transition-opacity">
            <CloseCircleGreyIcon className="w-8 h-8" />
          </button>
        </div>

        {/* Iframe */}
        <div className="relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin h-8 w-8 border-4 border-[#012169] border-t-transparent rounded-full" />
            </div>
          )}
          <iframe
            ref={iframeRef}
            className="w-full h-[600px]"
            src={paymentURL}
            title="Paystack Checkout"
            allow="payment"
            onLoad={handleIframeLoad}
          />
        </div>
      </div>
    </div>
    </ModalPortal>
  );
};

export { PaymentEmbed };
